import { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  BookOpen,
  ArrowUpDown,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { SearchableSelect } from '@/components/SearchableSelect';

const EMPTY_FORM = {
  first_name: '', last_name: '', designation: '',
  company_name: '', company_address: '', city: '', state: '', zip: '',
  email: '', company_phone: '', mobile_phone: '',
  license_number: '', responsible_for_filing_types: [],
  is_active: true, notes: '',
};

export default function ResourcesPage() {
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-primary/10 border border-primary/20">
          <BookOpen className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-[22px] font-medium tracking-tight" data-testid="resources-title">Resources</h1>
        </div>
      </div>

      <Tabs defaultValue="professionals" className="w-full">
        <TabsList>
          <TabsTrigger value="professionals" data-testid="resources-tab-professionals">
            Professionals
          </TabsTrigger>
        </TabsList>
        <TabsContent value="professionals" className="mt-4">
          <ProfessionalsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfessionalsTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('all');
  const [filterFilingType, setFilterFilingType] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [sortKey, setSortKey] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');
  const [designationOpts, setDesignationOpts] = useState(['PE', 'RA']);
  const [filingTypeOpts, setFilingTypeOpts] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get('/professionals').then(r => setItems(r.data)).catch(() => toast.error('Could not load professionals'));

  useEffect(() => {
    load();
    api.get('/professionals/designation-options').then(r => {
      setDesignationOpts([...(r.data.default || []), ...(r.data.custom || [])]);
    }).catch(() => {});
    api.get('/professionals/filing-type-options').then(r => {
      setFilingTypeOpts([...(r.data.default || []), ...(r.data.custom || [])]);
    }).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSheetOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      first_name: p.first_name || '', last_name: p.last_name || '',
      designation: p.designation || '',
      company_name: p.company_name || '', company_address: p.company_address || '',
      city: p.city || '', state: p.state || '', zip: p.zip || '',
      email: p.email || '', company_phone: p.company_phone || '', mobile_phone: p.mobile_phone || '',
      license_number: p.license_number || '',
      responsible_for_filing_types: p.responsible_for_filing_types || [],
      is_active: p.is_active !== false,
      notes: p.notes || '',
    });
    setSheetOpen(true);
  };

  const save = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) {
      return toast.error('First or last name required');
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/professionals/${editing.id}`, form);
        toast.success('Professional updated');
      } else {
        await api.post('/professionals', form);
        toast.success('Professional added');
      }
      setSheetOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/professionals/${deleteTarget.id}`);
      toast.success('Professional deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const addCustomDesignation = async (name) => {
    try {
      await api.post('/professionals/designation-options', { name });
      setDesignationOpts(prev => [...prev, name]);
      toast.success(`Added designation: ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add designation');
    }
  };
  const addCustomFilingType = async (name) => {
    try {
      await api.post('/professionals/filing-type-options', { name });
      setFilingTypeOpts(prev => [...prev, name]);
      toast.success(`Added filing type: ${name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add filing type');
    }
  };

  const toggleFilingType = (t) => {
    setForm(prev => {
      const has = (prev.responsible_for_filing_types || []).includes(t);
      return {
        ...prev,
        responsible_for_filing_types: has
          ? prev.responsible_for_filing_types.filter(x => x !== t)
          : [...(prev.responsible_for_filing_types || []), t],
      };
    });
  };

  const sortBy = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let rows = items.filter(p => {
      if (filterDesignation !== 'all' && p.designation !== filterDesignation) return false;
      if (filterActive === 'active' && !p.is_active) return false;
      if (filterActive === 'inactive' && p.is_active) return false;
      if (filterFilingType !== 'all' && !(p.responsible_for_filing_types || []).includes(filterFilingType)) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [
          p.first_name, p.last_name, p.company_name, p.email,
          p.city, p.state, p.license_number,
          (p.responsible_for_filing_types || []).join(' '),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
    rows = [...rows].sort((a, b) => {
      const av = (a[sortKey] || '').toString().toLowerCase();
      const bv = (b[sortKey] || '').toString().toLowerCase();
      const cmp = av.localeCompare(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [items, search, filterDesignation, filterFilingType, filterActive, sortKey, sortDir]);

  return (
    <Card className="bg-card border-border">
      {/* Filter bar */}
      <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, company, email, license..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 bg-background border-border h-9"
            data-testid="professionals-search"
          />
        </div>
        <Select value={filterDesignation} onValueChange={setFilterDesignation}>
          <SelectTrigger className="w-32 bg-background border-border h-9 text-xs" data-testid="filter-designation">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Designations</SelectItem>
            {designationOpts.map(d => <SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterFilingType} onValueChange={setFilterFilingType}>
          <SelectTrigger className="w-44 bg-background border-border h-9 text-xs" data-testid="filter-filing-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border max-h-72">
            <SelectItem value="all" className="text-xs">All Filing Types</SelectItem>
            {filingTypeOpts.map(t => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterActive} onValueChange={setFilterActive}>
          <SelectTrigger className="w-28 bg-background border-border h-9 text-xs" data-testid="filter-active">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All</SelectItem>
            <SelectItem value="active" className="text-xs">Active</SelectItem>
            <SelectItem value="inactive" className="text-xs">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{filtered.length} of {items.length}</span>
          <Button size="sm" onClick={openCreate} className="bg-primary text-primary-foreground h-9" data-testid="add-professional-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Professional
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <SortableHeader label="Name" k="last_name" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} />
              <TableHead className="text-xs">Designation</TableHead>
              <SortableHeader label="Company" k="company_name" sortKey={sortKey} sortDir={sortDir} onSort={sortBy} />
              <TableHead className="text-xs">Filing Types</TableHead>
              <TableHead className="text-xs">Email</TableHead>
              <TableHead className="text-xs">Phone</TableHead>
              <TableHead className="text-xs">License #</TableHead>
              <TableHead className="text-xs">Active</TableHead>
              <TableHead className="text-xs w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No professionals match.</TableCell></TableRow>
            ) : filtered.map(p => (
              <TableRow key={p.id} className="border-border hover:bg-muted/10" data-testid={`professional-row-${p.id}`}>
                <TableCell className="text-sm font-medium">
                  {[p.first_name, p.last_name].filter(Boolean).join(' ') || <span className="text-muted-foreground italic">No name</span>}
                </TableCell>
                <TableCell>
                  {p.designation ? <Badge variant="outline" className="text-xs border-blue-500/40 text-blue-400">{p.designation}</Badge> : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.company_name || '-'}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(p.responsible_for_filing_types || []).map(t => (
                      <Badge key={t} variant="secondary" className="text-xs bg-muted/40">{t}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {p.email ? <a href={`mailto:${p.email}`} className="text-blue-400 hover:underline">{p.email}</a> : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {p.company_phone || p.mobile_phone || '-'}
                </TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{p.license_number || '-'}</TableCell>
                <TableCell>
                  {p.is_active ? <Badge className="text-xs bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Active</Badge> : <Badge variant="outline" className="text-xs text-muted-foreground">Inactive</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEdit(p)} data-testid={`edit-professional-${p.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => setDeleteTarget(p)} data-testid={`delete-professional-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing ? 'Edit Professional' : 'Add Professional'}</SheetTitle>
            <SheetDescription>EOR/AOR reference record. All fields except name are optional.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First Name</Label>
                <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} className="bg-background border-border" data-testid="form-first-name" />
              </div>
              <div>
                <Label className="text-xs">Last Name</Label>
                <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} className="bg-background border-border" data-testid="form-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Designation</Label>
                <SearchableSelect
                  value={form.designation || ''}
                  onValueChange={v => setForm({ ...form, designation: v })}
                  options={designationOpts.map(d => ({ value: d, label: d }))}
                  placeholder="Select..."
                  searchPlaceholder="Search or add..."
                  allowCustom
                  onAddCustom={addCustomDesignation}
                  data-testid="form-designation"
                />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <Checkbox
                  checked={form.is_active}
                  onCheckedChange={v => setForm({ ...form, is_active: !!v })}
                  data-testid="form-active"
                />
                <Label className="text-xs cursor-pointer" onClick={() => setForm({ ...form, is_active: !form.is_active })}>
                  Active
                </Label>
              </div>
            </div>

            <div>
              <Label className="text-xs">Company Name</Label>
              <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} className="bg-background border-border" data-testid="form-company" />
            </div>
            <div>
              <Label className="text-xs">Company Address</Label>
              <Input value={form.company_address} onChange={e => setForm({ ...form, company_address: e.target.value })} className="bg-background border-border" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-background border-border" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="bg-background border-border" />
              </div>
              <div>
                <Label className="text-xs">Zip</Label>
                <Input value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} className="bg-background border-border" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" data-testid="form-email" />
              </div>
              <div>
                <Label className="text-xs">License Number</Label>
                <Input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} className="bg-background border-border" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Company Phone</Label>
                <Input value={form.company_phone} onChange={e => setForm({ ...form, company_phone: e.target.value })} className="bg-background border-border" />
              </div>
              <div>
                <Label className="text-xs">Mobile Phone</Label>
                <Input value={form.mobile_phone} onChange={e => setForm({ ...form, mobile_phone: e.target.value })} className="bg-background border-border" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Responsible for Filing Types</Label>
              <div className="flex flex-wrap gap-2 mt-2 p-3 rounded-md bg-background border border-border">
                {filingTypeOpts.map(t => {
                  const active = (form.responsible_for_filing_types || []).includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleFilingType(t)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${active ? 'bg-blue-500/20 border-blue-500/50 text-blue-300' : 'bg-muted/20 border-border text-muted-foreground hover:bg-muted/40'}`}
                      data-testid={`filing-type-toggle-${t}`}
                    >
                      {t}
                    </button>
                  );
                })}
                <AddFilingTypeInline existing={filingTypeOpts} onAdd={addCustomFilingType} />
              </div>
            </div>

            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} className="bg-background border-border" data-testid="form-notes" />
            </div>
          </div>
          <SheetFooter className="gap-2">
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-professional-btn">
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Professional'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Professional</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently remove <span className="font-semibold text-foreground">{deleteTarget ? [deleteTarget.first_name, deleteTarget.last_name].filter(Boolean).join(' ') : ''}</span> from the Resources list? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-professional">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function SortableHeader({ label, k, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  return (
    <TableHead
      onClick={() => onSort(k)}
      className="text-xs cursor-pointer select-none hover:text-foreground"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${active ? 'text-foreground' : 'text-muted-foreground/50'}`} />
        {active && <span className="text-xs text-muted-foreground">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </TableHead>
  );
}

function AddFilingTypeInline({ existing, onAdd }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-2.5 py-1 rounded-full border border-dashed border-border text-muted-foreground hover:bg-muted/30"
        data-testid="add-filing-type-btn"
      >
        <Plus className="h-3 w-3 inline mr-0.5" /> Add new
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === 'Enter' && val.trim() && !existing.includes(val.trim())) {
            await onAdd(val.trim());
            setVal('');
            setOpen(false);
          } else if (e.key === 'Escape') {
            setOpen(false); setVal('');
          }
        }}
        className="h-6 text-xs w-32 bg-background"
        placeholder="New type..."
      />
      <button type="button" onClick={() => { setOpen(false); setVal(''); }} className="text-muted-foreground hover:text-foreground">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
